(function() {
  "use strict";

  var RAILWAY_API_URL = "https://rinyabooks-server-production.up.railway.app";

  var root = document.getElementById("rinya-personalise-wizard");
  if (!root) return;

  var config = window.RINYA_CONFIG || {};
  var apiUrl = (config.apiUrl || RAILWAY_API_URL).replace(/\/+$/, '');
  var variantId = String(config.variantId || "").trim();

  var PRODUCTION_DAYS = parseInt(config.productionDays, 10);
  var COMPLETION_MINUTES = parseInt(config.completionMinutes, 10);
  var DELIVERY_RANGE = config.deliveryRange || "";
  var DELIVERY_ISO = config.deliveryIso || "";

  var STORAGE_KEY = "rinya_wizard_v1";
  /* A 48-megapixel phone photo clears 10 MB without trying, and the old limit
     turned those customers away on the first step of the purchase. The backend
     crops every photo to 1024x1024 anyway, so the bytes above this were never
     used for anything — they only had to be uploaded first. Kept in step with
     MAX_PHOTO_BYTES in services/utils.js on the server. */
  var MAX_PHOTO_BYTES = 25 * 1024 * 1024;
  var MAX_PHOTO_MB = Math.round(MAX_PHOTO_BYTES / 1048576);

  /* What the browser will even try to open. The ceiling above applies to what
     is uploaded, and shrinking happens first, so a 40 MB photo now becomes a
     1 MB upload instead of a refusal. This second, looser limit exists only
     because decoding something enormous can take a phone's tab down with it,
     and no amount of shrinking helps a file that cannot be read. */
  var MAX_SOURCE_BYTES = 60 * 1024 * 1024;
  var MAX_SOURCE_MB = Math.round(MAX_SOURCE_BYTES / 1048576);

  /* Photos above this are re-encoded in the browser before they are sent.
     2048px is twice what the backend keeps, so nothing visible is lost, and a
     phone photo lands at roughly a megabyte instead of ten — which on mobile
     data is the difference between a wait and an abandoned order. */
  var SHRINK_ABOVE_BYTES = 2 * 1024 * 1024;
  var SHRINK_MAX_EDGE = 2048;
  var SHRINK_QUALITY = 0.9;
  // Matches the backend's minimum, so an unusable photo is caught on the photo
  // step rather than on the last click of the journey.
  var MIN_PHOTO_PIXELS = 400;

  /* Mirrors the backend's upload filter instead of being stricter than it.
     The stricter list this replaced rejected HEIC here with a generic message,
     which hid the backend's specific advice naming the iPhone camera setting
     to change. The file picker still asks for JPG/PNG/WebP — that is what makes
     iOS hand over a converted JPEG — but drag-and-drop ignores `accept`, so
     files of any type still reach this function. */
  /* An emoji is several UTF-16 units long — "🎂" is two, "👨‍👩‍👧‍👦" is eleven — so
     counting with .length charges a customer eleven characters for one
     picture, and a maxlength cut can split one in half and leave a broken
     character behind. Count and cut what a reader would call a character. */
  var segmenter = null;
  try {
    if (window.Intl && Intl.Segmenter) segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
  } catch (e) { segmenter = null; }

  function graphemes(str) {
    var text = String(str == null ? "" : str);
    if (!text) return [];
    if (!segmenter) return Array.from(text);   // codepoints: never splits a pair
    var out = [];
    var it = segmenter.segment(text)[Symbol.iterator]();
    for (var next = it.next(); !next.done; next = it.next()) out.push(next.value.segment);
    return out;
  }

  function countChars(str) { return graphemes(str).length; }

  function cutToChars(str, max) {
    var parts = graphemes(str);
    return parts.length <= max ? String(str == null ? "" : str) : parts.slice(0, max).join("");
  }

  function isPlausiblePhoto(file) {
    var type = String(file.type || "").toLowerCase();
    // Some browsers report nothing at all for an unfamiliar extension
    if (!type) return true;
    return type.indexOf("image/") === 0 || type === "application/octet-stream";
  }

  var currentStep = 0;
  var state = {
    name: "",
    gender: "",
    photoFile: null,
    photoDataUrl: null,
    dedication: "",
    // Which edition of the book to print. Empty means "whatever this Shopify
    // product is", which is what a single-language book always gets.
    bookLanguage: "",
    // Deliberately not persisted, like the photo: consent is affirmed afresh
    // each visit rather than restored from a previous one.
    consent: false
  };

  /* Every language this book exists in, and whether a preview is offered,
     both come from `GET /api/preview/config` — so the number of steps is not
     a constant, and a new language or a change of mind about the preview
     needs no theme deploy. */
  var bookLanguages = [];

  /* Steps are named rather than numbered. They used to be fixed indices with
     goToStep(2) scattered about, which made inserting one a renumbering
     exercise across the whole file — and the language step has to be
     inserted conditionally, only when the book exists in more than one. */
  var STEP_TITLES = {
    language: "Språk",
    details: "Detaljer",
    photo: "Foto",
    dedication: "Dedikation",
    review: "Granska",
    preview: "Förhandsvisning"
  };

  function stepKeys() {
    var keys = [];
    // A choice of one is not a choice; a single-language book shows no step.
    if (bookLanguages.length > 1) keys.push("language");
    keys.push("details", "photo", "dedication", "review");
    if (preview.enabled) keys.push("preview");
    return keys;
  }

  function stepIndex(key) {
    var i = stepKeys().indexOf(key);
    return i < 0 ? 0 : i;
  }

  function currentKey() {
    return stepKeys()[currentStep] || "details";
  }

  function goToStepKey(key) {
    goToStep(stepIndex(key));
  }

  var preview = {
    enabled: false,
    bookId: null,
    pages: [],
    photoId: null,
    hash: null,
    states: {},
    uploading: false,
    uploadFailed: false,
    book: null,
    pollTimer: null,
    deadline: 0,
    preparedFor: null
  };

  /* What a given set of rendered pages belongs to. gender is in it because it
     is passed into the face-swap prompt: going back and changing it after
     uploading has to re-prepare, or the customer sees the wrong artwork. */
  function previewSignature() {
    return String(preview.photoId || "") + ":" + String(state.gender || "") +
      ":" + String(state.bookLanguage || "");
  }

  function stepLabels() {
    return stepKeys().map(function(k) { return STEP_TITLES[k]; });
  }

  /* ── Icons ──────────────────────────────────────
     Inline SVG replaces the previous emoji, which read as casual
     against the premium intent and rendered inconsistently per platform. */
  var ICONS = {
    girl: '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><circle cx="16" cy="12" r="6" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M6 28c0-5 4.5-8 10-8s10 3 10 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M9 11c1-4 3.5-6 7-6s6 2 7 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    boy: '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><circle cx="16" cy="12" r="6" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M6 28c0-5 4.5-8 10-8s10 3 10 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M10 9c2-2 10-2 12 0" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    camera: '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><rect x="3" y="9" width="26" height="18" rx="3" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M11 9l2-4h6l2 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><circle cx="16" cy="18" r="5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>',
    lock: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect x="3" y="7" width="10" height="7" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
    brush: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M11.5 2.5 13.5 4.5 7 11l-3 1 1-3z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M3 14h10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
    truck: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect x="1" y="4" width="9" height="7" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M10 6.5h2.6L15 8.8V11h-5z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><circle cx="4.5" cy="12.5" r="1.4" fill="none" stroke="currentColor" stroke-width="1.2"/><circle cx="11.5" cy="12.5" r="1.4" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>',
    check: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    cross: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>'
  };

  /* ── Analytics ──────────────────────────────────
     The audit asks for a minimum funnel sequence so the team can see
     where the journey breaks rather than only that conversion is low.
     Events go to dataLayer and to a DOM event, so any tag manager or
     app can subscribe without this file knowing about it. */
  function track(eventName, payload) {
    var detail = payload || {};
    detail.event = eventName;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(detail);
    } catch (e) { /* analytics must never block the journey */ }
    try {
      document.dispatchEvent(new CustomEvent("rinya:" + eventName, { detail: detail }));
    } catch (e) { /* older browsers */ }
  }

  /* ── Saved progress ─────────────────────────────
     The photo is deliberately never persisted: it is the most
     privacy-sensitive item and too large for session storage. */
  function saveProgress() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        name: state.name,
        gender: state.gender,
        dedication: state.dedication,
        bookLanguage: state.bookLanguage,
        step: currentStep
      }));
    } catch (e) { /* private mode or storage full */ }
  }

  function restoreProgress() {
    var raw;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;
    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!saved || typeof saved !== "object") return;
    state.name = typeof saved.name === "string" ? saved.name : "";
    state.gender = saved.gender === "girl" || saved.gender === "boy" ? saved.gender : "";
    state.dedication = typeof saved.dedication === "string" ? saved.dedication : "";
    // The photo is never restored, so never resume past the photo step.
    state.bookLanguage = typeof saved.bookLanguage === "string" ? saved.bookLanguage : "";
    // Never resume past the photo step: the photo itself is not restored.
    var step = parseInt(saved.step, 10);
    var limit = stepIndex("photo");
    currentStep = step > limit ? limit : (step > 0 ? step : 0);
  }

  function clearProgress() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* nothing to clear */ }
  }

  function el(tag, props) {
    var element = document.createElement(tag);
    if (props) {
      var keys = Object.keys(props);
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        if (key === "className") element.className = props[key];
        else if (key === "textContent") element.textContent = props[key];
        else if (key === "style" && typeof props[key] === "string") element.setAttribute("style", props[key]);
        else if (key === "innerHTML") element.innerHTML = props[key];
        else if (key.indexOf("aria-") === 0 || key === "role" || key === "type") element.setAttribute(key, props[key]);
        else element[key] = props[key];
      }
    }
    return element;
  }

  function lines(value) {
    return String(value || "")
      .split("\n")
      .map(function(s) { return s.trim(); })
      .filter(function(s) { return s.length > 0; });
  }

  /* Errors are announced to assistive technology. The audit found upload
     errors were shown visually only. */
  function errorNode(id) {
    return el("p", { className: "rinya-error", id: id, role: "alert", "aria-live": "assertive" });
  }

  function showError(id, message) {
    var node = document.getElementById(id);
    if (!node) return;
    // Re-setting identical text does not always re-announce; clear first.
    node.textContent = "";
    node.textContent = message;
  }

  function goToStep(n) {
    currentStep = n;
    saveProgress();
    render();
    track("personalisation_step_reached", { step: n + 1, step_name: stepLabels()[n] });
    var heading = root.querySelector(".rinya-step__title");
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    }
    root.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function render() {
    root.innerHTML = "";
    root.appendChild(buildHeader());
    // The preview step shows artwork rather than a form, so it gets the room.
    var card = el("div", {
      className: currentKey() === "preview" ? "rinya-card rinya-card--wide" : "rinya-card"
    });
    card.appendChild(buildStepIndicator());
    var content = el("div", { className: "rinya-step-content" });
    var key = currentKey();
    if (key === "language") content.appendChild(buildStepLanguage());
    else if (key === "details") content.appendChild(buildStepDetails());
    else if (key === "photo") content.appendChild(buildStepPhoto());
    else if (key === "dedication") content.appendChild(buildStepDedication());
    else if (key === "review") content.appendChild(buildStepReview());
    else if (key === "preview") content.appendChild(buildStepPreview());
    card.appendChild(content);
    root.appendChild(card);
    root.appendChild(buildTrustFooter());
  }

  function buildHeader() {
    return el("div", { className: "rinya-header" });
  }

  /* Redraw only the step indicator. Re-rendering the whole step would throw
     away whatever the customer has typed since the page loaded. */
  function refreshIndicator() {
    var indicator = root.querySelector(".rinya-steps");
    if (indicator && indicator.parentNode) {
      indicator.parentNode.replaceChild(buildStepIndicator(), indicator);
    }
  }

  function buildStepIndicator() {
    var wrap = el("div", {
      className: "rinya-steps",
      role: "list",
      "aria-label": "Så här långt har du kommit"
    });
    for (var i = 0; i < stepLabels().length; i++) {
      var isLast = i === stepLabels().length - 1;
      var item = el("div", {
        className: "rinya-steps__item" + (isLast ? "" : " rinya-steps__item--expandable"),
        role: "listitem"
      });
      var node = el("div", { className: "rinya-steps__node" });
      var circleClass = "rinya-steps__circle ";
      var stateWord;
      if (i < currentStep) { circleClass += "rinya-steps__circle--done"; stateWord = "klart"; }
      else if (i === currentStep) { circleClass += "rinya-steps__circle--active"; stateWord = "pågående"; }
      else { circleClass += "rinya-steps__circle--upcoming"; stateWord = "kommande"; }

      var circle = el("div", { className: circleClass, "aria-hidden": "true" });
      if (i < currentStep) circle.innerHTML = ICONS.check;
      else circle.textContent = String(i + 1);
      if (i === currentStep) circle.setAttribute("aria-current", "step");
      node.appendChild(circle);

      var labelClass = "rinya-steps__label" + (i === currentStep ? " rinya-steps__label--active" : "");
      node.appendChild(el("span", { className: labelClass, "aria-hidden": "true", textContent: stepLabels()[i] }));
      node.appendChild(el("span", {
        className: "rinya-visually-hidden",
        textContent: "Steg " + (i + 1) + " av " + stepLabels().length + ": " + stepLabels()[i] + ", " + stateWord
      }));

      item.appendChild(node);
      if (!isLast) {
        var lineClass = "rinya-steps__line " + (i < currentStep ? "rinya-steps__line--done" : "rinya-steps__line--upcoming");
        item.appendChild(el("div", { className: lineClass, "aria-hidden": "true" }));
      }
      wrap.appendChild(item);
    }
    return wrap;
  }

  /* Sets expectations about effort before the customer starts,
     which the audit flags as a driver of abandonment. */
  function buildEffortNote() {
    if (!(COMPLETION_MINUTES > 0)) return null;
    return el("p", {
      className: "rinya-effort-note",
      textContent: "Tar ungefär " + COMPLETION_MINUTES + " minuter. Dina uppgifter sparas medan du fyller i."
    });
  }

  /** The label the customer saw, for the cart property and the order email. */
  function bookLanguageLabel() {
    for (var i = 0; i < bookLanguages.length; i++) {
      if (bookLanguages[i].language === state.bookLanguage) return bookLanguages[i].label;
    }
    return "";
  }

  /* Only reachable when the book exists in more than one language — stepKeys
     leaves it out otherwise, so this is never a list of one. */
  function buildStepLanguage() {
    var frag = document.createDocumentFragment();
    frag.appendChild(el("h2", { className: "rinya-step__title", textContent: "Vilket språk ska boken vara på?" }));
    frag.appendChild(el("p", {
      className: "rinya-step__desc",
      textContent: "Berättelsen är densamma — bara texten i boken byter språk."
    }));

    var list = el("div", { className: "rinya-langs", role: "radiogroup", "aria-label": "Bokens språk" });

    bookLanguages.forEach(function(lang) {
      var selected = state.bookLanguage === lang.language;
      var card = el("button", {
        type: "button",
        className: "rinya-lang" + (selected ? " is-selected" : ""),
        role: "radio"
      });
      card.setAttribute("aria-checked", selected ? "true" : "false");

      card.appendChild(el("span", { className: "rinya-lang__name", textContent: lang.label }));
      // The title as it will actually be printed, so the choice is concrete
      // rather than an abstract language name.
      card.appendChild(el("span", {
        className: "rinya-lang__title",
        textContent: lang.title.replace(/\{\{Name\}\}/g, state.name || "…")
      }));

      card.addEventListener("click", function() {
        state.bookLanguage = lang.language;
        // The chosen edition decides which pages a preview renders, so any
        // work already done for the other one has to be dropped.
        resetPreview();
        saveProgress();
        render();
        track("book_language_selected", { language: lang.language });
      });

      list.appendChild(card);
    });

    frag.appendChild(list);
    frag.appendChild(errorNode("step-language-error"));

    var btnRow = el("div", { className: "rinya-btn-row" });
    var nextBtn = el("button", { type: "button", className: "rinya-btn rinya-btn--primary", textContent: "Fortsätt" });
    nextBtn.addEventListener("click", function() {
      if (!state.bookLanguage) {
        showError("step-language-error", "Välj ett språk för att fortsätta");
        return;
      }
      goToStepKey("details");
    });
    btnRow.appendChild(nextBtn);
    frag.appendChild(btnRow);
    return frag;
  }

  function buildStepDetails() {
    var frag = document.createDocumentFragment();
    frag.appendChild(el("h2", { className: "rinya-step__title", textContent: "Vem ska boken handla om?" }));
    var effort = buildEffortNote();
    if (effort) frag.appendChild(effort);

    var nameGroup = el("div", { className: "rinya-field" });
    var nameInput = el("input", {
      type: "text",
      className: "rinya-input",
      placeholder: "t.ex. Astrid",
      id: "rinya-name",
      maxLength: 20
    });
    nameGroup.appendChild(el("label", {
      className: "rinya-label",
      htmlFor: "rinya-name",
      textContent: "Barnets förnamn"
    }));
    nameInput.value = state.name;
    nameInput.setAttribute("aria-describedby", "name-char-count");
    nameInput.addEventListener("input", function(e) {
      state.name = e.target.value;
      var counter = document.getElementById("name-char-count");
      if (counter) counter.textContent = state.name.length + "/20 tecken";
      saveProgress();
    });
    nameInput.addEventListener("focus", function() {
      nameInput.classList.remove("rinya-input--error");
      nameInput.removeAttribute("aria-invalid");
    });
    nameGroup.appendChild(nameInput);
    nameGroup.appendChild(el("span", {
      className: "rinya-char-count",
      id: "name-char-count",
      textContent: state.name.length + "/20 tecken"
    }));
    frag.appendChild(nameGroup);

    var genderGroup = el("div", { className: "rinya-field" });
    var genderLegend = el("span", { className: "rinya-label", id: "rinya-gender-label", textContent: "Kön" });
    genderGroup.appendChild(genderLegend);

    var cards = el("div", {
      className: "rinya-gender-group",
      role: "radiogroup",
      "aria-labelledby": "rinya-gender-label"
    });
    var genderOptions = [
      { value: "girl", icon: ICONS.girl, label: "Flicka" },
      { value: "boy", icon: ICONS.boy, label: "Pojke" }
    ];

    for (var g = 0; g < genderOptions.length; g++) {
      (function(opt) {
        var isSelected = state.gender === opt.value;
        var btn = el("button", {
          type: "button",
          role: "radio",
          "aria-checked": isSelected ? "true" : "false",
          className: "rinya-gender-card rinya-gender-card--" + opt.value + (isSelected ? " rinya-gender-card--selected" : "")
        });
        btn.appendChild(el("span", { className: "rinya-gender-card__icon", "aria-hidden": "true", innerHTML: opt.icon }));
        btn.appendChild(el("span", { className: "rinya-gender-card__label", textContent: opt.label }));
        btn.addEventListener("click", function() {
          state.gender = opt.value;
          saveProgress();
          render();
          var restored = root.querySelector(".rinya-gender-card--" + opt.value);
          if (restored) restored.focus({ preventScroll: true });
        });
        cards.appendChild(btn);
      })(genderOptions[g]);
    }

    genderGroup.appendChild(cards);
    frag.appendChild(genderGroup);
    frag.appendChild(errorNode("step1-error"));

    var btn = el("button", { type: "button", className: "rinya-btn rinya-btn--primary", textContent: "Fortsätt" });
    btn.addEventListener("click", function() {
      if (!state.name.trim()) {
        showError("step1-error", "Ange barnets namn");
        nameInput.classList.add("rinya-input--error");
        nameInput.setAttribute("aria-invalid", "true");
        nameInput.focus();
        return;
      }
      if (!state.gender) {
        showError("step1-error", "Välj kön");
        var first = root.querySelector(".rinya-gender-card");
        if (first) first.focus();
        return;
      }
      goToStepKey("photo");
    });
    frag.appendChild(btn);
    return frag;
  }

  /* The audit found no practical good-photo versus poor-photo guidance,
     on the step with the highest effort and the highest drop-off. */
  function buildPhotoGuide() {
    var good = lines(config.photoTipsGood);
    var bad = lines(config.photoTipsBad);
    if (!good.length && !bad.length) return null;

    var guide = el("div", { className: "rinya-photo-guide" });
    var columns = [
      { items: good, title: "Fungerar bäst", modifier: "good", icon: ICONS.check },
      { items: bad, title: "Undvik", modifier: "bad", icon: ICONS.cross }
    ];
    for (var c = 0; c < columns.length; c++) {
      var col = columns[c];
      if (!col.items.length) continue;
      var box = el("div", { className: "rinya-photo-guide__col rinya-photo-guide__col--" + col.modifier });
      box.appendChild(el("h3", { className: "rinya-photo-guide__title", textContent: col.title }));
      var list = el("ul", { className: "rinya-photo-guide__list" });
      for (var i = 0; i < col.items.length; i++) {
        var li = el("li", { className: "rinya-photo-guide__item" });
        li.appendChild(el("span", { className: "rinya-photo-guide__icon", "aria-hidden": "true", innerHTML: col.icon }));
        li.appendChild(el("span", { textContent: col.items[i] }));
        list.appendChild(li);
      }
      box.appendChild(list);
      guide.appendChild(box);
    }
    return guide;
  }

  function buildPrivacyBanner() {
    var text = config.privacyText || "";
    if (!text) return null;
    var banner = el("div", { className: "rinya-privacy-banner" });
    banner.appendChild(el("span", { className: "rinya-privacy-banner__icon", "aria-hidden": "true", innerHTML: ICONS.lock }));
    var body = el("div");
    body.appendChild(el("p", { className: "rinya-privacy-banner__text", textContent: text }));
    if (config.privacyLinkUrl) {
      body.appendChild(el("a", {
        className: "rinya-privacy-banner__link",
        href: config.privacyLinkUrl,
        textContent: config.privacyLinkLabel || "Så hanterar vi fotot"
      }));
    }
    banner.appendChild(body);
    return banner;
  }

  function buildStepPhoto() {
    var frag = document.createDocumentFragment();
    frag.appendChild(el("h2", { className: "rinya-step__title", textContent: "Ladda upp ett porträtt" }));
    frag.appendChild(el("p", {
      className: "rinya-step__desc",
      textContent: "Ett tydligt foto framifrån ger bäst resultat."
    }));

    var guide = buildPhotoGuide();
    if (guide) frag.appendChild(guide);

    if (!state.photoDataUrl) {
      var zone = el("div", {
        className: "rinya-upload-zone",
        id: "upload-zone",
        role: "button",
        tabindex: 0,
        "aria-describedby": "upload-hint"
      });
      var fileInput = el("input", {
        type: "file",
        accept: "image/jpeg,image/png,image/webp",
        className: "rinya-visually-hidden",
        id: "photo-input"
      });
      fileInput.addEventListener("change", function(e) {
        handlePhotoFile(e.target.files[0]);
      });
      zone.appendChild(fileInput);
      zone.appendChild(el("div", { className: "rinya-upload-zone__icon", "aria-hidden": "true", innerHTML: ICONS.camera }));
      zone.appendChild(el("p", { className: "rinya-upload-zone__title", textContent: "Tryck för att välja foto" }));
      zone.appendChild(el("p", { className: "rinya-upload-zone__hint", id: "upload-hint", textContent: "JPG, PNG eller WebP – max " + MAX_PHOTO_MB + " MB" }));
      zone.addEventListener("click", function() { fileInput.click(); });
      zone.addEventListener("keydown", function(e) {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
          e.preventDefault();
          fileInput.click();
        }
      });
      zone.addEventListener("dragover", function(e) { e.preventDefault(); zone.classList.add("rinya-upload-zone--dragover"); });
      zone.addEventListener("dragleave", function() { zone.classList.remove("rinya-upload-zone--dragover"); });
      zone.addEventListener("drop", function(e) {
        e.preventDefault();
        zone.classList.remove("rinya-upload-zone--dragover");
        handlePhotoFile(e.dataTransfer.files[0]);
      });
      frag.appendChild(zone);
    } else {
      var thumb = el("div", { className: "rinya-photo-preview" });
      var circle = el("div", { className: "rinya-photo-preview__circle" });
      var img = el("img", { className: "rinya-photo-preview__img", alt: "Valt porträtt av barnet" });
      img.src = state.photoDataUrl;
      circle.appendChild(img);
      thumb.appendChild(circle);
      var removeBtn = el("button", { type: "button", className: "rinya-photo-preview__remove", textContent: "Ta bort och välj ett annat" });
      removeBtn.addEventListener("click", function() {
        state.photoFile = null;
        state.photoDataUrl = null;
        resetPreview();
        render();
        var zoneEl = document.getElementById("upload-zone");
        if (zoneEl) zoneEl.focus({ preventScroll: true });
      });
      thumb.appendChild(removeBtn);
      frag.appendChild(thumb);
    }

    var banner = buildPrivacyBanner();
    if (banner) frag.appendChild(banner);

    /* Guardian confirmation and consent to the automated image processing.
       It used to sit on the review step, which was correct while the photo
       stayed in the browser until the final click. It no longer does: the
       preview has to upload the photo here so the face swaps have time to run
       while the customer writes their dedication. So the tick moves to where
       the photo is chosen, and nothing is sent until it is ticked. The wording
       is a section setting so it can be revised without a code change. */
    var consentText = config.consentText || "";
    if (consentText) {
      var consentField = el("label", { className: "rinya-consent", htmlFor: "rinya-consent" });
      var consentBox = el("input", { type: "checkbox", className: "rinya-consent__box", id: "rinya-consent" });
      consentBox.checked = state.consent;
      consentBox.setAttribute("aria-describedby", "step2-error");
      consentBox.addEventListener("change", function(e) {
        state.consent = e.target.checked;
        if (state.consent) showError("step2-error", "");
      });
      consentField.appendChild(consentBox);
      consentField.appendChild(el("span", { className: "rinya-consent__text", textContent: consentText }));
      frag.appendChild(consentField);
    }

    frag.appendChild(errorNode("step2-error"));

    var btnRow = el("div", { className: "rinya-btn-row" });
    var backBtn = el("button", { type: "button", className: "rinya-btn rinya-btn--secondary", textContent: "Tillbaka" });
    backBtn.addEventListener("click", function() { goToStepKey("details"); });
    btnRow.appendChild(backBtn);
    var nextBtn = el("button", { type: "button", className: "rinya-btn rinya-btn--primary", id: "photo-next-btn", textContent: "Fortsätt" });
    nextBtn.addEventListener("click", function() {
      if (!state.photoDataUrl) {
        showError("step2-error", "Ladda upp ett foto för att fortsätta");
        return;
      }
      if (config.consentText && !state.consent) {
        showError("step2-error", "Kryssa i samtycket för att kunna gå vidare.");
        track("photo_step_blocked", { reason: "missing_consent" });
        var box = document.getElementById("rinya-consent");
        if (box) box.focus({ preventScroll: true });
        return;
      }

      /* This is the moment the photograph leaves the browser — after the tick,
         never before. Sending it now rather than at checkout is what buys the
         face swaps the thirty seconds the customer spends on the next two
         steps, which is the whole reason the preview does not feel like a
         wait. */
      if (!preview.enabled || preview.preparedFor === previewSignature()) {
        goToStepKey("dedication");
        return;
      }
      startPreviewUpload(nextBtn);
    });
    btnRow.appendChild(nextBtn);
    frag.appendChild(btnRow);
    return frag;
  }

  /* Upload the photo, then ask the backend to start rendering. Both are
     fire-and-forget from the customer's point of view: whatever happens, they
     move on to the dedication. A preview that cannot be produced costs them a
     nicety, not the purchase — so a failure here downgrades to the four-step
     wizard rather than blocking it. */
  function startPreviewUpload(btn) {
    if (preview.uploading) return;
    preview.uploading = true;

    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
    var originalLabel = btn.textContent;
    btn.innerHTML = '<span class="rinya-spinner" aria-hidden="true"></span> Förbereder...';

    var form = new FormData();
    form.append("photo", state.photoFile);
    if (config.consentText) {
      // The consent is recorded where it is given, next to the photo it
      // covers, and travels with the upload if this becomes an order.
      form.append("consent", "true");
      form.append("consentText", config.consentText);
    }

    function moveOn() {
      preview.uploading = false;
      goToStepKey("dedication");
    }

    fetch(apiUrl + "/api/upload-photo", { method: "POST", mode: "cors", body: form })
      .then(function(res) {
        if (!res.ok) {
          return res.json()
            .catch(function() { throw new Error("Upload failed (" + res.status + ")"); })
            .then(function(d) { throw new Error((d && d.error) || "Upload failed"); });
        }
        return res.json();
      })
      .then(function(data) {
        preview.photoId = data.photoId;
        track("preview_photo_uploaded", {});
        return fetch(apiUrl + "/api/preview/prepare", {
          method: "POST",
          mode: "cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            photoId: data.photoId,
            handle: config.productHandle || "",
            gender: state.gender,
            language: state.bookLanguage || ""
          })
        });
      })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(data) {
        if (data && data.hash) {
          if (data.bookId) preview.bookId = data.bookId;
          preview.hash = data.hash;
          preview.pages = data.pages || preview.pages;
          preview.preparedFor = previewSignature();
          preview.deadline = Date.now() + 60000;
          pollPreviewStatus();
          track("preview_requested", { pages: preview.pages.length });
        } else {
          preview.enabled = false;
        }
        moveOn();
      })
      .catch(function(err) {
        /* The photo will be sent again with the order, so nothing is lost but
           the preview. Say so once, quietly, and carry on. */
        preview.uploadFailed = true;
        preview.enabled = false;
        btn.innerHTML = "";
        btn.textContent = originalLabel;
        btn.disabled = false;
        btn.removeAttribute("aria-busy");
        track("preview_failed", { stage: "upload", message: String(err && err.message) });
        moveOn();
      });
  }

  /* Re-encode an oversized photo before it leaves the phone.

     The backend crops every photo to 1024x1024, so the megapixels above that
     are uploaded and then thrown away — on mobile data that is a wait long
     enough to lose the sale. This hands `done` a much smaller file, or the
     original if anything at all goes wrong: the ceiling still accepts it, and
     a slow upload beats a failed one.

     Orientation is the trap here. A phone writes "rotate 90" into the photo's
     EXIF rather than rotating the pixels, and re-encoding through a canvas
     drops EXIF — so a browser that does not apply the flag when it decodes
     would hand the printer a sideways child. Browsers have applied it since
     image-orientation: from-image became the default, which is exactly what
     the support test below asks. Anything older simply uploads the original.  */
  function shrinkPhoto(file, img, done) {
    if (file.size <= SHRINK_ABOVE_BYTES) return done(file, img.src);
    if (!(window.CSS && CSS.supports && CSS.supports("image-orientation", "from-image"))) {
      return done(file, img.src);
    }

    var w = img.naturalWidth;
    var h = img.naturalHeight;
    var scale = Math.min(1, SHRINK_MAX_EDGE / Math.max(w, h));
    /* Scaling a panorama by its long edge can push the short edge under the
       minimum the backend accepts, so that minimum wins over the target. */
    if (Math.round(Math.min(w, h) * scale) < MIN_PHOTO_PIXELS) {
      scale = Math.min(1, MIN_PHOTO_PIXELS / Math.min(w, h));
    }

    var canvas;
    try {
      canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      var ctx = canvas.getContext("2d");
      if (!ctx || !canvas.toBlob || typeof File !== "function") return done(file, img.src);
      /* White first: a PNG with transparency would otherwise come out of the
         JPEG encoder on black. */
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    } catch (e) {
      return done(file, img.src);
    }

    canvas.toBlob(function(blob) {
      if (!blob || blob.size >= file.size) return done(file, img.src);

      var shrunk;
      try {
        shrunk = new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", {
          type: "image/jpeg",
          lastModified: Date.now()
        });
      } catch (e) {
        return done(file, img.src);
      }

      /* The preview on screen switches to the smaller image too, so the full
         data URL of a 25 MB photo is not left sitting in memory on a phone. */
      var reader = new FileReader();
      reader.onload = function(ev) {
        track("photo_shrunk", {
          from_bytes: file.size,
          to_bytes: shrunk.size,
          width: canvas.width,
          height: canvas.height
        });
        done(shrunk, ev.target.result);
      };
      reader.onerror = function() { done(file, img.src); };
      reader.readAsDataURL(shrunk);
    }, "image/jpeg", SHRINK_QUALITY);
  }

  function handlePhotoFile(file) {
    if (!file) return;
    if (!isPlausiblePhoto(file)) {
      showError("step2-error", "Ladda upp en JPG, PNG eller WebP-bild");
      track("photo_upload_failed", { reason: "invalid_type", file_type: file.type || "unknown" });
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      showError("step2-error", "Bilden är för stor för att öppnas (" + (file.size / 1048576).toFixed(1) +
        " MB). Den får vara högst " + MAX_SOURCE_MB + " MB.");
      track("photo_upload_failed", { reason: "too_large_to_open", file_size: file.size });
      return;
    }
    var reader = new FileReader();
    reader.onload = function(e) {
      var dataUrl = e.target.result;

      /* The backend refuses a photo under 400x400, but not until the final
         "Lägg i varukorgen". Measuring here means the customer hears it while
         still on the photo step, with the picker one tap away. A file the
         browser cannot decode is left to the backend, which identifies the
         format and explains what to do about it. */
      var probe = new Image();
      probe.onload = function() {
        if (probe.naturalWidth < MIN_PHOTO_PIXELS || probe.naturalHeight < MIN_PHOTO_PIXELS) {
          showError("step2-error", "Fotot är för litet (" + probe.naturalWidth + "×" + probe.naturalHeight +
            " pixlar). Det behöver vara minst " + MIN_PHOTO_PIXELS + "×" + MIN_PHOTO_PIXELS + " pixlar.");
          track("photo_upload_failed", {
            reason: "too_small",
            width: probe.naturalWidth,
            height: probe.naturalHeight
          });
          return;
        }
        /* The ceiling is applied to what will actually be sent, after the
           shrink has had its turn — a 40 MB photo leaves here as about a
           megabyte, and only a file that is still too big after all that is
           refused. */
        shrinkPhoto(file, probe, function(ready, readyUrl) {
          if (ready.size > MAX_PHOTO_BYTES) {
            showError("step2-error", "Bilden får vara högst " + MAX_PHOTO_MB + " MB. Din bild är " +
              (ready.size / 1048576).toFixed(1) + " MB.");
            track("photo_upload_failed", { reason: "too_large", file_size: ready.size });
            return;
          }
          acceptPhoto(ready, readyUrl);
        });
      };
      probe.onerror = function() {
        /* The browser could not decode the file at all. By far the most common
           cause is an iPhone HEIC, so say so here rather than showing a broken
           preview and failing on the last click. Browsers that *can* render
           HEIC take the path above; the backend stays the authority and answers
           those with the same advice at upload time. */
        showError("step2-error", "Bilden kunde inte visas. Kommer fotot från en iPhone är det " +
          "troligen i HEIC-format — ändra till ”Mest kompatibelt” under Inställningar › Kamera › " +
          "Format, eller spara om fotot som JPG och försök igen.");
        track("photo_upload_failed", { reason: "undecodable", file_type: file.type || "unknown" });
      };
      probe.src = dataUrl;
    };
    reader.onerror = function() {
      showError("step2-error", "Bilden kunde inte läsas. Försök med en annan fil.");
      track("photo_upload_failed", { reason: "read_error" });
    };
    reader.readAsDataURL(file);
  }

  function acceptPhoto(file, dataUrl) {
    state.photoFile = file;
    state.photoDataUrl = dataUrl;
    showError("step2-error", "");
    track("photo_selected", { file_size: file.size, file_type: file.type });
    render();
  }

  function resetPreview() {
    stopPolling();
    preview.photoId = null;
    preview.hash = null;
    preview.states = {};
    preview.preparedFor = null;
    preview.uploadFailed = false;
    if (preview.book) {
      preview.book.destroy();
      preview.book = null;
    }
  }

  function stopPolling() {
    if (preview.pollTimer) {
      clearTimeout(preview.pollTimer);
      preview.pollTimer = null;
    }
  }

  /* Polling rather than a socket: this is a handful of requests over half a
     minute, and generation is detached from any of them — moving between steps
     never cancels a swap we have already paid for. */
  function pollPreviewStatus() {
    stopPolling();
    if (!preview.hash || !preview.bookId) return;

    var url = apiUrl + "/api/preview/status"
      + "?hash=" + encodeURIComponent(preview.hash)
      + "&bookId=" + encodeURIComponent(preview.bookId)
      + "&gender=" + encodeURIComponent(state.gender || "pojke");

    fetch(url, { mode: "cors" })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(data) {
        if (!data || !data.pages) throw new Error("no status");

        for (var i = 0; i < data.pages.length; i++) {
          preview.states[data.pages[i].page] = data.pages[i].state;
        }
        applyPreviewStates();

        if (data.done) return;

        /* A hard ceiling, because "still working" forever is worse than an
           honest "this one did not come out". Anything unfinished at the
           deadline is shown as failed and the sale carries on. */
        if (Date.now() > preview.deadline) {
          markUnfinishedFailed();
          track("preview_failed", { stage: "timeout" });
          return;
        }
        preview.pollTimer = setTimeout(pollPreviewStatus, 1500);
      })
      .catch(function() {
        if (Date.now() > preview.deadline) {
          markUnfinishedFailed();
          return;
        }
        preview.pollTimer = setTimeout(pollPreviewStatus, 3000);
      });
  }

  function markUnfinishedFailed() {
    stopPolling();
    for (var i = 0; i < preview.pages.length; i++) {
      var page = preview.pages[i];
      if (preview.states[page] !== "ready") preview.states[page] = "failed";
    }
    applyPreviewStates();
  }

  /* The blurred stand-in for a page. Stock artwork, no photo involved, so it
     is available before any face swap has run — which is what lets the wait
     show the page filling in rather than an empty frame. */
  function placeholderUrl(page) {
    return apiUrl + "/api/preview/placeholder"
      + "?bookId=" + encodeURIComponent(preview.bookId)
      + "&page=" + encodeURIComponent(page);
  }

  function previewImageUrl(page) {
    return apiUrl + "/api/preview/" + encodeURIComponent(preview.hash)
      + "?bookId=" + encodeURIComponent(preview.bookId)
      + "&page=" + encodeURIComponent(page)
      + "&name=" + encodeURIComponent(state.name)
      + "&gender=" + encodeURIComponent(state.gender || "pojke")
      // The name and dedication live in the URL, so editing them simply
      // changes the address and refetches. The artwork underneath is cached
      // and already paid for; only the text is redrawn.
      + "&dedikering=" + encodeURIComponent(state.dedication || "");
  }

  function readyCount() {
    var n = 0;
    for (var i = 0; i < preview.pages.length; i++) {
      if (preview.states[preview.pages[i]] === "ready") n++;
    }
    return n;
  }

  /* Pages settle in one at a time rather than all at the end: seeing the title
     page a second after arriving is what makes the remaining wait bearable. */
  function applyPreviewStates() {
    updatePreviewCounter();
    if (!preview.book) return;

    for (var i = 0; i < preview.pages.length; i++) {
      var page = preview.pages[i];
      var pageState = preview.states[page];
      if (pageState === "ready") {
        preview.book.setPage(i, {
          state: "ready",
          src: previewImageUrl(page),
          alt: "Uppslag " + (i + 1) + " ur " + state.name + "s bok",
          failMessage: config.previewFailedText
        });
      } else if (pageState === "failed") {
        preview.book.setPage(i, { state: "failed", message: config.previewFailedText });
      }
    }
  }

  function updatePreviewCounter() {
    var node = document.getElementById("preview-counter");
    if (!node) return;

    var ready = readyCount();
    var total = preview.pages.length;
    var failed = 0;
    for (var i = 0; i < total; i++) {
      if (preview.states[preview.pages[i]] === "failed") failed++;
    }

    node.innerHTML = "";
    if (ready + failed < total) {
      node.appendChild(el("span", { className: "rinya-spinner", "aria-hidden": "true" }));
      node.appendChild(el("span", { textContent: "Ritar upp boken — " + ready + " av " + total + " uppslag klara" }));
    } else if (failed === total) {
      node.appendChild(el("span", { textContent: config.previewFailedText || "Förhandsvisningen kunde inte skapas just nu." }));
    } else if (failed > 0) {
      node.appendChild(el("span", { textContent: ready + " av " + total + " uppslag klara. Resten visas inte just nu — boken påverkas inte." }));
    } else {
      node.appendChild(el("span", { textContent: "Alla " + total + " uppslag klara" }));
    }
  }

  function buildStepPreview() {
    var frag = document.createDocumentFragment();
    frag.appendChild(el("h2", { className: "rinya-step__title", textContent: "Så här blir boken" }));
    frag.appendChild(el("p", {
      className: "rinya-step__desc",
      textContent: "Din bok växer fram — bläddra neråt genom de första uppslagen med " + state.name + " i bilderna."
    }));

    var viewer = el("div", { id: "preview-viewer" });
    frag.appendChild(viewer);

    /* aria-live, because the page count changing is the only signal that
       anything is happening for someone who cannot see the pages settle in. */
    var counter = el("p", {
      className: "rinya-preview__counter",
      id: "preview-counter",
      role: "status",
      "aria-live": "polite"
    });
    frag.appendChild(counter);

    if (config.previewDisclaimer) {
      var note = el("div", { className: "rinya-preview__disclaimer" });
      var paragraphs = lines(config.previewDisclaimer);
      for (var i = 0; i < paragraphs.length; i++) {
        note.appendChild(el("p", { textContent: paragraphs[i] }));
      }
      frag.appendChild(note);
    }

    frag.appendChild(errorNode("step5-error"));

    var btnRow = el("div", { className: "rinya-btn-row" });
    var backBtn = el("button", { type: "button", className: "rinya-btn rinya-btn--secondary", textContent: "Tillbaka" });
    backBtn.addEventListener("click", function() { goToStepKey("review"); });
    btnRow.appendChild(backBtn);

    /* Never disabled while pages render. Somebody who has decided should not
       have to wait for a nicety before they can buy. */
    var submitBtn = el("button", { type: "button", className: "rinya-btn rinya-btn--primary", id: "submit-btn", textContent: "Lägg i varukorgen" });
    submitBtn.addEventListener("click", handleSubmit);
    btnRow.appendChild(submitBtn);
    frag.appendChild(btnRow);

    frag.appendChild(el("p", {
      className: "rinya-visually-hidden",
      id: "submit-status",
      role: "status",
      "aria-live": "polite"
    }));

    // Built after this fragment is in the document, so the flipbook can
    // measure its own stage.
    setTimeout(function() {
      var host = document.getElementById("preview-viewer");
      if (!host || !window.RinyaPageFeed) return;
      preview.book = window.RinyaPageFeed.create(host, {
        pageCount: preview.pages.length,
        label: "Förhandsvisning av " + state.name + "s bok",
        labelFor: function(i) { return "Uppslag " + (i + 1) + " av " + preview.pages.length; },
        placeholderFor: function(i) { return placeholderUrl(preview.pages[i]); },
        waitingText: "Vänta lite."
      });
      applyPreviewStates();
    }, 0);

    return frag;
  }

  function buildStepDedication() {
    var frag = document.createDocumentFragment();
    var MAX_LEN = 150;
    frag.appendChild(el("h2", { className: "rinya-step__title", textContent: "Lägg till en dedikation" }));
    frag.appendChild(el("p", {
      className: "rinya-step__desc",
      textContent: "Ett personligt meddelande som trycks på andra sidan."
    }));

    var textGroup = el("div", { className: "rinya-field" });
    var textarea = document.createElement("textarea");
    textarea.className = "rinya-textarea";
    textarea.id = "rinya-dedication";
    textarea.rows = 5;
    textarea.placeholder = "Till vår älskade " + (state.name || "lilla hjälte") + ",\nMed all vår kärlek...";
    // No maxlength attribute: the browser enforces it in UTF-16 units, which
    // both under-counts the customer's allowance and can cut an emoji in half
    textarea.setAttribute("aria-label", "Dedikation");
    textarea.value = state.dedication;
    textarea.setAttribute("aria-describedby", "ded-char-count");
    textGroup.appendChild(el("label", {
      className: "rinya-visually-hidden",
      htmlFor: "rinya-dedication",
      textContent: "Dedikation, högst " + MAX_LEN + " tecken"
    }));
    textarea.addEventListener("input", function(e) {
      var trimmed = cutToChars(e.target.value, MAX_LEN);
      // Only write back when something was actually over the limit — assigning
      // to value moves the caret to the end, which would fight mid-sentence edits
      if (trimmed !== e.target.value) e.target.value = trimmed;
      state.dedication = trimmed;
      var counter = document.getElementById("ded-char-count");
      if (counter) counter.textContent = countChars(state.dedication) + "/" + MAX_LEN + " tecken";
      saveProgress();
      updateDedicationPreview();
    });
    textGroup.appendChild(textarea);
    textGroup.appendChild(el("span", {
      className: "rinya-char-count",
      id: "ded-char-count",
      textContent: countChars(state.dedication) + "/" + MAX_LEN + " tecken"
    }));
    frag.appendChild(textGroup);

    frag.appendChild(el("div", { id: "ded-preview-wrap" }));
    frag.appendChild(el("p", { className: "rinya-optional-note", textContent: "Detta steg är valfritt." }));

    var btnRow = el("div", { className: "rinya-btn-row" });
    var backBtn = el("button", { type: "button", className: "rinya-btn rinya-btn--secondary", textContent: "Tillbaka" });
    backBtn.addEventListener("click", function() { goToStepKey("photo"); });
    btnRow.appendChild(backBtn);
    var nextBtn = el("button", { type: "button", className: "rinya-btn rinya-btn--primary", textContent: "Fortsätt" });
    nextBtn.addEventListener("click", function() { goToStepKey("review"); });
    btnRow.appendChild(nextBtn);
    frag.appendChild(btnRow);

    setTimeout(updateDedicationPreview, 0);
    return frag;
  }

  function updateDedicationPreview() {
    var wrap = document.getElementById("ded-preview-wrap");
    if (!wrap) return;
    wrap.innerHTML = "";
    if (state.dedication) {
      var quoteCard = el("div", { className: "rinya-dedication-preview" });
      quoteCard.appendChild(el("div", { className: "rinya-dedication-preview__quote", "aria-hidden": "true", innerHTML: "&#8220;" }));
      quoteCard.appendChild(el("p", { className: "rinya-dedication-preview__text", textContent: state.dedication }));
      wrap.appendChild(quoteCard);
    }
  }

  function buildStepReview() {
    var frag = document.createDocumentFragment();
    frag.appendChild(el("h2", { className: "rinya-step__title", textContent: "Granska din bok" }));
    frag.appendChild(el("p", {
      className: "rinya-step__desc",
      textContent: "Kontrollera att allt ser rätt ut innan du lägger boken i varukorgen."
    }));

    var card = el("div", { className: "rinya-review-card" });

    var header = el("div", { className: "rinya-review-card__header" });
    if (state.photoDataUrl) {
      var photoWrap = el("div", { className: "rinya-review-card__photo" });
      var photoImg = el("img", { alt: "Valt porträtt av barnet" });
      photoImg.src = state.photoDataUrl;
      photoWrap.appendChild(photoImg);
      header.appendChild(photoWrap);
    }
    var headerText = el("div");
    headerText.appendChild(el("p", { className: "rinya-review-card__name", textContent: state.name + "s bok" }));
    var pronoun = state.gender === "girl" ? "henne" : "honom";
    headerText.appendChild(el("p", { className: "rinya-review-card__subtitle", textContent: "En personlig berättelse för " + pronoun }));
    header.appendChild(headerText);
    card.appendChild(header);

    var body = el("div", { className: "rinya-review-card__body" });
    var genderLabel = state.gender === "girl" ? "Flicka" : "Pojke";
    var rows = [
      { label: "Namn", value: state.name },
      { label: "Kön", value: genderLabel },
      { label: "Dedikation", value: state.dedication || "Ingen", isDedication: true }
    ];
    // Only worth a row when there was a choice to make.
    if (bookLanguages.length > 1) {
      rows.splice(2, 0, { label: "Bokens språk", value: bookLanguageLabel() });
    }
    for (var r = 0; r < rows.length; r++) {
      var row = el("div", { className: "rinya-review-card__row" });
      row.appendChild(el("span", { className: "rinya-review-card__label", textContent: rows[r].label }));
      var valClass = "rinya-review-card__value";
      if (rows[r].isDedication && state.dedication) valClass += " rinya-review-card__value--dedication";
      row.appendChild(el("span", { className: valClass, textContent: rows[r].value }));
      body.appendChild(row);
    }
    card.appendChild(body);
    frag.appendChild(card);

    /* Total cost and a real arrival date at the point of decision,
       rather than an abstract delivery speed. */
    if (DELIVERY_RANGE) {
      var delivery = el("p", { className: "rinya-delivery-note" });
      delivery.appendChild(el("span", { className: "rinya-delivery-note__icon", "aria-hidden": "true", innerHTML: ICONS.truck }));
      delivery.appendChild(el("span", { textContent: "Beräknad leverans " }));
      var time = el("time", { className: "rinya-delivery-note__date", textContent: DELIVERY_RANGE });
      if (DELIVERY_ISO) time.setAttribute("datetime", DELIVERY_ISO);
      delivery.appendChild(time);
      frag.appendChild(delivery);
    }

    /* Distansavtalslagen 2 kap. 11 § removes the 14-day right of withdrawal
       for goods made to the customer's specification, but only where the
       customer was told so before ordering. This is that notice, and it sits
       on the last step because that is the last moment before the order. */
    if (config.withdrawalNotice) {
      var withdrawal = el("p", {
        className: "rinya-withdrawal-note",
        textContent: config.withdrawalNotice
      });
      frag.appendChild(withdrawal);
    }

    frag.appendChild(errorNode("step4-error"));

    var btnRow = el("div", { className: "rinya-btn-row" });
    var backBtn = el("button", { type: "button", className: "rinya-btn rinya-btn--secondary", textContent: "Tillbaka" });
    backBtn.addEventListener("click", function() { goToStepKey("dedication"); });
    btnRow.appendChild(backBtn);

    if (preview.enabled) {
      var previewBtn = el("button", {
        type: "button",
        className: "rinya-btn rinya-btn--primary",
        textContent: "Se förhandsvisning"
      });
      previewBtn.addEventListener("click", function() { goToStepKey("preview"); });
      btnRow.appendChild(previewBtn);
    } else {
      var submitBtn = el("button", { type: "button", className: "rinya-btn rinya-btn--primary", id: "submit-btn", textContent: "Lägg i varukorgen" });
      submitBtn.addEventListener("click", handleSubmit);
      btnRow.appendChild(submitBtn);
    }
    frag.appendChild(btnRow);

    var status = el("p", {
      className: "rinya-visually-hidden",
      id: "submit-status",
      role: "status",
      "aria-live": "polite"
    });
    frag.appendChild(status);
    return frag;
  }

  function setStatus(message) {
    var node = document.getElementById("submit-status");
    if (node) node.textContent = message;
  }

  function handleSubmit() {
    var btn = document.getElementById("submit-btn");
    if (!btn || btn.disabled) return;

    // Without a variant the cart request silently fails, so stop here
    // with an explanation rather than at Shopify's endpoint.
    var errorId = currentKey() === "preview" ? "step5-error" : "step4-error";

    var parsedVariant = parseInt(variantId, 10);
    if (!parsedVariant) {
      showError(errorId, "Boken kunde inte identifieras. Gå tillbaka till produktsidan och börja om.");
      track("add_to_cart_failed", { reason: "missing_variant" });
      return;
    }

    /* Consent is checked on the photo step now, because that is where the
       photo is sent. Re-checked here because a customer who restored a saved
       session, or whose upload fell back to the file branch below, has not
       necessarily passed through it in this visit. */
    if (config.consentText && !state.consent) {
      showError(errorId, "Kryssa i samtycket för att kunna gå vidare.");
      track("add_to_cart_failed", { reason: "missing_consent" });
      goToStepKey("photo");
      var consentBox = document.getElementById("rinya-consent");
      if (consentBox) consentBox.focus({ preventScroll: true });
      return;
    }

    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
    btn.innerHTML = '<span class="rinya-spinner" aria-hidden="true"></span> Laddar upp foto...';
    setStatus("Laddar upp foto");
    showError(errorId, "");
    track("personalisation_completed", { has_dedication: state.dedication.length > 0 });

    var formData = new FormData();
    /* Prefer promoting the upload the preview already made: the same bytes are
       on the server, keyed by the hash the rendered pages sit under, and
       sending the file a second time would only cost the customer another
       wait. The file branch stays for a session where the upload never
       happened — preview off, or an upload that failed. */
    if (preview.photoId) {
      formData.append("photoId", preview.photoId);
    } else {
      formData.append("photo", state.photoFile);
    }
    formData.append("name", state.name);
    if (state.bookLanguage) formData.append("bookLanguage", state.bookLanguage);
    formData.append("gender", state.gender);
    formData.append("dedication", state.dedication || "");
    if (config.consentText) {
      // Recorded server-side with a timestamp, so what was agreed to is
      // evidenced alongside the photo it covers rather than only in the DOM.
      formData.append("consent", "true");
      formData.append("consentText", config.consentText);
    }

    fetch(apiUrl + "/api/personalise", {
      method: "POST",
      mode: "cors",
      body: formData
    })
    .then(function(res) {
      if (!res.ok) {
        return res.json()
          .catch(function() { throw new Error("Upload failed (" + res.status + ")"); })
          .then(function(d) { throw new Error((d && d.error) || "Upload failed"); });
      }
      return res.json();
    })
    .then(function(data) {
      btn.innerHTML = '<span class="rinya-spinner" aria-hidden="true"></span> Lägger till...';
      setStatus("Lägger boken i varukorgen");

      // _order_ref is the contract with the fulfilment backend: the
      // orders/paid webhook uses it to find the uploaded photo and the
      // personalisation data. It must survive every future change.
      return fetch(config.cartAddUrl || "/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{
            id: parsedVariant,
            quantity: 1,
            properties: {
              "_order_ref": data.orderRef,
              "Barnets namn": state.name,
              "Kön": state.gender === "girl" ? "Flicka" : "Pojke",
              // Shown to the customer on the order and in the confirmation
              // email, so it carries the label rather than the code.
              "Bokens språk": bookLanguageLabel() || "Svenska",
              "Dedikation": state.dedication || "(ingen)"
            }
          }]
        })
      });
    })
    .then(function(cartRes) {
      if (!cartRes.ok) throw new Error("Cart add failed: " + cartRes.status);
      track("added_to_cart", { variant_id: parsedVariant });
      clearProgress();
      window.location.href = config.cartUrl || "/cart";
    })
    .catch(function(err) {
      btn.disabled = false;
      btn.removeAttribute("aria-busy");
      btn.textContent = "Lägg i varukorgen";
      setStatus("");
      showError(errorId, "Något gick fel: " + err.message + ". Dina uppgifter finns kvar, försök igen.");
      track("add_to_cart_failed", { reason: "request_error", message: String(err && err.message) });
    });
  }

  function buildTrustFooter() {
    var footer = el("div", { className: "rinya-trust" });
    var items = [];

    if (config.trustCraft) items.push({ icon: ICONS.brush, text: config.trustCraft });

    // The previous copy read "Tryckt och skickat inom dagar" — the number
    // was missing entirely. It now comes from the section settings.
    if (PRODUCTION_DAYS > 0) {
      items.push({ icon: ICONS.truck, text: "Trycks inom " + PRODUCTION_DAYS + " arbetsdagar" });
    }
    if (config.trustPayment) items.push({ icon: ICONS.lock, text: config.trustPayment });

    for (var i = 0; i < items.length; i++) {
      var item = el("span", { className: "rinya-trust__item" });
      item.appendChild(el("span", { className: "rinya-trust__icon", "aria-hidden": "true", innerHTML: items[i].icon }));
      item.appendChild(el("span", { textContent: items[i].text }));
      footer.appendChild(item);
    }
    return footer;
  }

  /* Without a variant nothing can be added to the cart, so the journey cannot
     end in a purchase however well it is filled in. That used to surface only
     on the last click, after the photo and the dedication were done. Saying it
     first costs the customer nothing and points them at a book instead. */
  function renderMissingVariantNotice() {
    root.innerHTML = "";
    var card = el("div", { className: "rinya-card rinya-shell" });
    card.appendChild(el("h1", {
      className: "rinya-shell__title",
      textContent: "Välj en bok först"
    }));
    card.appendChild(el("p", {
      className: "rinya-shell__note",
      textContent: "Personaliseringen börjar från bokens sida, så att vi vet vilken bok det gäller."
    }));
    var link = el("a", {
      className: "rinya-btn rinya-btn--primary",
      textContent: "Se våra böcker"
    });
    link.setAttribute("href", config.booksUrl || "/collections/all");
    card.appendChild(link);
    root.appendChild(card);
  }

  restoreProgress();

  if (!parseInt(variantId, 10)) {
    renderMissingVariantNotice();
    track("personalisation_blocked", { reason: "missing_variant" });
    return;
  }

  /* A restored session never resumes past the photo step — restoreProgress
     clamps it, because the photo itself is deliberately not persisted. That
     also keeps the preview step unreachable without a prepared render. */
  render();
  track("personalisation_started", { resumed: currentStep > 0 });
  track("personalisation_step_reached", { step: currentStep + 1, step_name: stepLabels()[currentStep] });

  /* Asked once, at the end of boot: the answer only matters by the time the
     customer leaves the photo step, and the first paint must not wait on a
     network round trip. The step indicator gains its fifth step when the
     answer arrives. */
  /* A missing preview is invisible to a shopper and should stay that way, but
     it must not be invisible to whoever runs the shop. Every branch below that
     ends without a preview says which one it was, so "the preview isn't
     showing" is answered by opening the console rather than by guesswork. */
  function noPreview(reason) {
    if (window.console && console.info) {
      console.info("[Rinya] Förhandsvisning visas inte: " + reason);
    }
  }

  fetch(apiUrl + "/api/preview/config?handle=" + encodeURIComponent(config.productHandle || ""), { mode: "cors" })
    .then(function(res) {
      if (!res.ok) {
        noPreview(res.status === 404
          ? "servern saknar /api/preview/config — den har inte driftsatts än."
          : "servern svarade " + res.status + " på /api/preview/config.");
        return null;
      }
      return res.json();
    })
    .then(function(data) {
      if (!data) return;

      /* The languages this book exists in. Defaulting to the first — the
         product's own edition — means a customer who never opens the step
         still gets the book they were looking at. Independent of the
         preview, so it is applied before any of the early returns below. */
      if (data.languages && data.languages.length) {
        /* Adding a step at the *front* shifts every index after it, so which
           step the customer is on has to be preserved by name. On a fresh
           visit there is nothing to preserve and they should start on the new
           first step — which needs a full render, not just the indicator. */
        var wasKey = currentKey();
        var untouched = currentStep === 0 && !state.name && !state.photoDataUrl;

        bookLanguages = data.languages;
        if (!state.bookLanguage) state.bookLanguage = data.languages[0].language;

        if (untouched) {
          currentStep = 0;
          render();
        } else {
          currentStep = stepIndex(wasKey);
          refreshIndicator();
        }
      }

      if (!data.enabled) {
        noPreview("avstängd på servern (sätt PREVIEW_ENABLED=true).");
        return;
      }
      if (!data.bookId || !data.pages || !data.pages.length) {
        noPreview("boken \"" + (config.productHandle || "(okänd)") +
          "\" har inga uppslag markerade för förhandsvisning.");
        return;
      }
      preview.enabled = true;
      preview.bookId = data.bookId;
      preview.pages = data.pages;
      refreshIndicator();
    })
    .catch(function(err) {
      // Typically CORS or the API being unreachable. The four-step wizard is
      // unaffected either way.
      noPreview("kunde inte nå " + apiUrl + " (" + String(err && err.message) + ").");
    });

})();
